import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, type ReactElement, type ReactNode, useCallback } from 'react'

import { TechnicalError } from '@orus.eu/error'
import { borderRadius, borderStroke, colorTokens, cornerRibbonZIndex, shadow, spacing, Text } from '@orus.eu/pharaoh'
import type { PlatFormRole } from '@orus.eu/right-access-management'

import { coBrandingFactories, useEmbeddingPartnerControls } from '../../lib/embedding-partner.ts'
import {
  isE2eEnvironment,
  isLocalDevEnvironment,
  isProductionEnvironment,
  isReviewOrProductionEnvironment,
} from '../../lib/env-utils.ts'
import {
  hideEnvironmentRibbonTemporarily,
  useEnvironmentRibbonHidden,
} from '../../lib/environment-ribbon-visibility.ts'
import { type HoverProps, useGroupHover } from '../../lib/hooks/use-group-hover.ts'
import { useSession } from '../../lib/session.ts'

type PageProps = {
  children: ReactNode
}

export function Page({ children }: PageProps): ReactElement {
  return (
    <>
      {children}
      <EnvironmentHelper />
      <ImpersonationHelper />
    </>
  )
}

function EnvironmentHelper(): ReactNode {
  if (isE2eEnvironment()) {
    return null
  }

  if (isProductionEnvironment()) {
    return <ProductionRibbon />
  }

  if (isReviewOrProductionEnvironment()) {
    return <EnvironmentRibbonWithPartnerCycle name="Review Env" colorScale={colorTokens['color-bg-success-primary']} />
  }

  if (isLocalDevEnvironment()) {
    return <EnvironmentRibbonWithPartnerCycle name="Local Dev" colorScale={colorTokens['color-bg-neutral']} />
  }

  throw new TechnicalError('Unexpected environment type')
}

// Reads roles despite domain/permissions-not-roles: the ribbon tells the tech and ops profiles they are acting on
// production data, so it follows who these people are, not what they are allowed to do.
const PRODUCTION_RIBBON_ROLES: readonly PlatFormRole[] = ['techAdmin', 'businessAdmin', 'businessSpecialist']

function ProductionRibbon(): ReactNode {
  const session = useSession()

  const hasProductionRibbonRole =
    session.permissions.type === 'platform' &&
    session.permissions.rolesPermissions.some(({ role }) => PRODUCTION_RIBBON_ROLES.includes(role))

  if (!hasProductionRibbonRole) return null

  return <EnvironmentRibbon name="Production" colorScale={colorTokens['color-bg-danger-primary']} />
}

type EnvironmentRibbonProps = {
  name: string
  colorScale: string
  hoverProps?: HoverProps
}

function EnvironmentRibbon({ name, colorScale, hoverProps }: EnvironmentRibbonProps): ReactNode {
  // On click the ribbon will disappear for 10 seconds, so we can interact with the content behind
  // We don't hide forever, because we don't want old tabs to be confused with prod
  const hidden = useEnvironmentRibbonHidden()

  if (hidden) {
    return null
  }

  return (
    <div
      css={css`
        cursor: pointer;
        color: ${colorTokens['color-bg-base-normal']};
        background-color: ${colorScale};
        border: ${borderStroke[20]} solid ${colorTokens['color-bg-base-secondary']};
        box-shadow: ${shadow.bottom[20]};
        position: fixed;
        top: 0;
        left: 0;
        padding: ${spacing[30]} 100px;
        width: 320px;
        transform: translate(-50%, -50%) rotate(-45deg) translateY(80px);
        text-align: center;
        z-index: ${cornerRibbonZIndex};
      `}
      onClick={hideEnvironmentRibbonTemporarily}
      {...hoverProps}
    >
      <Text variant="subtitle" element="div">
        {name}
      </Text>
      <Text variant="body2" element="div">
        Click to hide
      </Text>
    </div>
  )
}

/**
 * Cycle order: every registered partner, then the unbranded Orus default (forced,
 * so it shows even when the URL or a contract pins a partner).
 * Driving the `EmbeddingPartnerWrapper` (rather than `setTheme`) switches theme +
 * co-branding (logos, fonts, illustrations, forced colors) together.
 */
const partnerIds = Object.keys(coBrandingFactories)

/**
 * Wraps the {@link EnvironmentRibbon} (Local Dev or Review Env) with a small
 * partner-cycle button that slides in below the banner when the dev hovers it.
 * Lets us validate partner co-branding (theme + fonts + logos + illustrations)
 * on the fly without cluttering the UI by default.
 *
 * The cycle button is only revealed on client pages, since it's only meaningful
 * there. The ribbon itself stays visible everywhere (same behaviour as before).
 */
function EnvironmentRibbonWithPartnerCycle({ name, colorScale }: { name: string; colorScale: string }): ReactNode {
  const session = useSession()
  const { embeddingPartnerId, setEmbeddingPartnerId, forceOrusDefault } = useEmbeddingPartnerControls()
  const { isHovered, hoverProps } = useGroupHover(500)

  const cyclePartner = useCallback(() => {
    const nextIndex = embeddingPartnerId ? partnerIds.indexOf(embeddingPartnerId) + 1 : 0
    if (nextIndex < partnerIds.length) setEmbeddingPartnerId(partnerIds[nextIndex])
    else forceOrusDefault()
  }, [embeddingPartnerId, setEmbeddingPartnerId, forceOrusDefault])

  const showCycleButton = session.permissions?.type === 'client' && isHovered

  return (
    <>
      <EnvironmentRibbon name={name} colorScale={colorScale} hoverProps={hoverProps} />
      <div
        css={css`
          position: fixed;
          top: 160px;
          left: ${showCycleButton ? '-8px' : '-250px'};
          z-index: ${cornerRibbonZIndex};
          transition: all 0.2s ease-in-out;
        `}
        {...hoverProps}
      >
        <ThemeCycleButton type="button" aria-label="Cycle embedding partner" onClick={cyclePartner}>
          <Text variant="caption" color={colorTokens['color-text-decorative-basic']}>
            CO-BRANDING
          </Text>
          <Text variant="body2" color={colorTokens['color-text-decorative-basic']}>
            <b>{embeddingPartnerId ?? 'orus (default)'}</b>
          </Text>
        </ThemeCycleButton>
      </div>
    </>
  )
}

const ImpersonationHelper = memo(function ImpersonationHelper() {
  const session = useSession()

  if (!session.impersonatorId) return null

  return (
    <ImpersonationOverlay>
      <Text variant="body2SemiBold">Impersonification en cours</Text>
    </ImpersonationOverlay>
  )
})

const ImpersonationOverlay = styled.div`
  color: ${colorTokens['color-bg-base-normal']};
  background-color: ${colorTokens['color-bg-danger-primary']};
  position: fixed;
  top: 0;
  left: 0;
  width: 500px;
  transform: translate(-50%, -50%) rotate(-45deg) translateY(120px);
  text-align: center;
  z-index: ${cornerRibbonZIndex};
`

const ThemeCycleButton = styled.button`
  text-align: left;
  padding: ${spacing[30]} ${spacing[50]};
  border-radius: ${borderRadius[20]};
  border: ${borderStroke[20]} solid ${colorTokens['color-bg-base-secondary']};
  background-color: ${colorTokens['color-bg-brand']};
  box-shadow: ${shadow.bottom[20]};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    filter: brightness(0.96);
  }

  &:active {
    filter: brightness(0.92);
  }
`
